import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Eye, EyeOff } from 'lucide-react';
import Spinner from '@/components/ui/Spinner';
import {
  AgentSettings,
  UpdateAgentSettingsPayload,
  getAgentSettings,
  updateAgentSettings,
} from '@/services/agentSettingsService';

type MaskedField = 'anthropicApiKey' | 'openaiApiKey' | 'qdrantApiKey';

const MASKED_FIELDS: MaskedField[] = ['anthropicApiKey', 'openaiApiKey', 'qdrantApiKey'];

type MaskedApiKeyFieldProps = {
  id: string;
  label: string;
  description?: string;
  rawValue: string | null;
  value: string;
  editing: boolean;
  showValue: boolean;
  onToggleShow: () => void;
  onUnlock: () => void;
  onChange: (value: string) => void;
};

function MaskedApiKeyField({
  id,
  label,
  description,
  rawValue,
  value,
  editing,
  showValue,
  onToggleShow,
  onUnlock,
  onChange,
}: MaskedApiKeyFieldProps) {
  const { t } = useTranslation();
  const isMasked = rawValue !== null && !editing;

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700" htmlFor={id}>
        {label}
      </label>
      {isMasked ? (
        <div className="flex items-center gap-2">
          <input
            id={id}
            type="password"
            value={value}
            readOnly
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-gray-100 font-mono"
          />
          <button
            type="button"
            onClick={onUnlock}
            className="whitespace-nowrap px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded"
          >
            {t('changeApiKey')}
          </button>
        </div>
      ) : (
        <div className="relative">
          <input
            id={id}
            type={showValue ? 'text' : 'password'}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="sk-..."
            className="w-full rounded-md border border-gray-300 px-3 py-2 pr-10 text-sm focus:ring-2 focus:ring-green-200 outline-none"
          />
          <button
            type="button"
            onClick={onToggleShow}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showValue ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      )}
      {description && <p className="text-xs text-gray-500">{description}</p>}
      <p className="text-xs text-gray-500">{t('apiKeyStoredSecurely')}</p>
    </div>
  );
}

export default function AgentSettingsManage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<AgentSettings>({
    queryKey: ['agentSettings'],
    queryFn: getAgentSettings,
    refetchOnWindowFocus: false,
  });

  const [values, setValues] = useState<Record<MaskedField, string>>({
    anthropicApiKey: '',
    openaiApiKey: '',
    qdrantApiKey: '',
  });
  const [editing, setEditing] = useState<Record<MaskedField, boolean>>({
    anthropicApiKey: false,
    openaiApiKey: false,
    qdrantApiKey: false,
  });
  const [showValue, setShowValue] = useState<Record<MaskedField, boolean>>({
    anthropicApiKey: false,
    openaiApiKey: false,
    qdrantApiKey: false,
  });
  const [qdrantUrl, setQdrantUrl] = useState('');
  const [qdrantUrlTouched, setQdrantUrlTouched] = useState(false);

  useEffect(() => {
    if (!data) return;
    setValues({
      anthropicApiKey: data.anthropicApiKey ?? '',
      openaiApiKey: data.openaiApiKey ?? '',
      qdrantApiKey: data.qdrantApiKey ?? '',
    });
    setEditing({
      anthropicApiKey: false,
      openaiApiKey: false,
      qdrantApiKey: false,
    });
    setShowValue({
      anthropicApiKey: false,
      openaiApiKey: false,
      qdrantApiKey: false,
    });
    setQdrantUrl(data.qdrantUrl ?? '');
    setQdrantUrlTouched(false);
  }, [data]);

  const unlockField = (field: MaskedField) => {
    setEditing((prev) => ({ ...prev, [field]: true }));
    setValues((prev) => ({ ...prev, [field]: '' }));
  };

  const onChangeMaskedField = (field: MaskedField, value: string) => {
    setEditing((prev) => ({ ...prev, [field]: true }));
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const toggleShowValue = (field: MaskedField) => {
    setShowValue((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const isDirty =
    MASKED_FIELDS.some((field) => editing[field] && values[field]) || qdrantUrlTouched;

  const { mutate: save, isPending: isSaving } = useMutation({
    mutationFn: updateAgentSettings,
    onSuccess: () => {
      toast.success(t('agentSettingsSaved'));
      queryClient.invalidateQueries({ queryKey: ['agentSettings'] });
    },
    onError: () => {
      toast.error(t('agentSettingsSaveError'));
    },
  });

  const handleSave = () => {
    const payload: UpdateAgentSettingsPayload = {};
    for (const field of MASKED_FIELDS) {
      if (editing[field] && values[field]) {
        payload[field] = values[field];
      }
    }
    if (qdrantUrlTouched) {
      payload.qdrantUrl = qdrantUrl;
    }
    if (Object.keys(payload).length === 0) return;
    save(payload);
  };

  return (
    <div className="p-6 w-full">
      {(isLoading || isSaving) && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <Spinner size="lg" color="primary" />
        </div>
      )}
      <div className="max-w-3xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('agentSettingsTitle')}</h1>
          <p className="text-sm text-gray-600">{t('agentSettingsDescription')}</p>
        </div>

        <section className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <MaskedApiKeyField
            id="anthropic-api-key"
            label={t('anthropicApiKeyLabel')}
            rawValue={data?.anthropicApiKey ?? null}
            value={values.anthropicApiKey}
            editing={editing.anthropicApiKey}
            showValue={showValue.anthropicApiKey}
            onToggleShow={() => toggleShowValue('anthropicApiKey')}
            onUnlock={() => unlockField('anthropicApiKey')}
            onChange={(value) => onChangeMaskedField('anthropicApiKey', value)}
          />

          <MaskedApiKeyField
            id="openai-api-key"
            label={t('openaiApiKeyLabel')}
            description={t('openaiApiKeyDescription')}
            rawValue={data?.openaiApiKey ?? null}
            value={values.openaiApiKey}
            editing={editing.openaiApiKey}
            showValue={showValue.openaiApiKey}
            onToggleShow={() => toggleShowValue('openaiApiKey')}
            onUnlock={() => unlockField('openaiApiKey')}
            onChange={(value) => onChangeMaskedField('openaiApiKey', value)}
          />

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700" htmlFor="qdrant-url">
              {t('qdrantUrlLabel')}
            </label>
            <input
              id="qdrant-url"
              type="text"
              value={qdrantUrl}
              onChange={(e) => {
                setQdrantUrl(e.target.value);
                setQdrantUrlTouched(true);
              }}
              placeholder="https://xxxx.qdrant.io"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-green-200 outline-none"
            />
          </div>

          <MaskedApiKeyField
            id="qdrant-api-key"
            label={t('qdrantApiKeyLabel')}
            rawValue={data?.qdrantApiKey ?? null}
            value={values.qdrantApiKey}
            editing={editing.qdrantApiKey}
            showValue={showValue.qdrantApiKey}
            onToggleShow={() => toggleShowValue('qdrantApiKey')}
            onUnlock={() => unlockField('qdrantApiKey')}
            onChange={(value) => onChangeMaskedField('qdrantApiKey', value)}
          />
        </section>

        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={!isDirty || isSaving}
            onClick={handleSave}
            className={`px-4 py-2 rounded text-white flex items-center gap-2 ${
              !isDirty || isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {isSaving && <Spinner size="sm" color="white" />}
            {t('save')}
          </button>
        </div>
      </div>
    </div>
  );
}
